"use client";

import type { TargetAndTransition, Transition } from "motion/react";
import { motion, useReducedMotion } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/cn";
import {
	SQ_PRIMARY_CTA_GRADIENT_ACTIVE_CLASS,
	SQ_PRIMARY_CTA_GRADIENT_CLASS,
	SQ_PRIMARY_CTA_GRADIENT_HOVER_CLASS,
} from "../../lib/sq-color-runtime-exports";
import { Button, type ButtonProps, buttonVariants } from "../button";
import { Spinner } from "../spinner";

const RIPPLE_DIAMETER_PX = 20;
const RIPPLE_DURATION_MS = 600;

interface Ripple {
	id: number;
	x: number;
	y: number;
}

export type RippleButtonAppearance = "default" | "plain";

export type RippleButtonProps = ButtonProps & {
	appearance?: RippleButtonAppearance;
	/** Passed to `motion.button` (not a DOM attribute on the public type otherwise). */
	initial?: React.ComponentProps<typeof motion.button>["initial"];
	/** Passed to `motion.button` for resting / selected motion state. */
	animate?: React.ComponentProps<typeof motion.button>["animate"];
	/** Replaces default hover motion (default: scale 1.03). Use `false` to disable. */
	whileHover?: TargetAndTransition | false;
	/** Replaces default tap motion (default: scale 0.97). Use `false` to disable. */
	whileTap?: TargetAndTransition | false;
	/** Additional classes for the ripple span. */
	rippleClassName?: string;
	/** Visual scale of the ripple animation (multiplier). */
	scale?: number;
	/** Motion transition for the ripple animation. */
	transition?: Transition;
};

type NativeDragProps =
	| "onDrag"
	| "onDragStart"
	| "onDragEnd"
	| "onDragEnter"
	| "onDragLeave"
	| "onDragOver"
	| "onDrop";

type NativeAnimationProps =
	| "onAnimationStart"
	| "onAnimationEnd"
	| "onAnimationIteration";

/**
 * `motion.button` uses `onDrag` for Motion’s drag gesture, which conflicts with React’s native
 * `onDrag` (DragEventHandler), and uses `onAnimationStart` for Motion’s animation lifecycle,
 * which conflicts with React’s native animation event handler types. Strip native drag/animation
 * props from the public API to avoid type collisions when spreading `...props` onto `motion.button`.
 */
export type SafeRippleButtonProps = Omit<
	RippleButtonProps,
	NativeDragProps | NativeAnimationProps
>;

export const RippleButton = React.forwardRef<
	HTMLButtonElement,
	SafeRippleButtonProps
>(function RippleButton(
	{
		className,
		onClick,
		onPointerDown,
		rippleClassName,
		children,
		variant,
		size,
		asChild = false,
		loading,
		disabled,
		appearance = "default",
		initial,
		animate,
		whileHover: whileHoverProp,
		whileTap: whileTapProp,
		scale = 10,
		transition = { duration: 0.6, ease: "easeOut" },
		...props
	},
	ref,
) {
	const [ripples, setRipples] = React.useState<Ripple[]>([]);
	const hostRef = React.useRef<HTMLButtonElement | null>(null);
	/** True when the current gesture already spawned a ripple from pointer down (avoids double ripples on click). */
	const rippleFromPointerRef = React.useRef(false);
	const prefersReducedMotion = useReducedMotion();

	const setButtonRef = React.useCallback(
		(node: HTMLButtonElement | null) => {
			hostRef.current = node;
			if (typeof ref === "function") {
				ref(node);
			} else if (ref != null) {
				(ref as React.RefObject<HTMLButtonElement | null>).current =
					node;
			}
		},
		[ref],
	);

	const createRipple = React.useCallback(
		(
			event: Pick<
				React.MouseEvent<HTMLButtonElement>,
				"clientX" | "clientY"
			>,
		) => {
			const element = hostRef.current;
			if (!element) {
				return;
			}
			const rect = element.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const y = event.clientY - rect.top;
			const id = Date.now() + Math.floor(Math.random() * 1000);
			const ripple: Ripple = { id, x, y };

			setRipples((prev) => [...prev, ripple]);

			window.setTimeout(() => {
				setRipples((prev) => prev.filter((r) => r.id !== id));
			}, RIPPLE_DURATION_MS);
		},
		[],
	);

	const handlePointerDown = React.useCallback(
		(event: React.PointerEvent<HTMLButtonElement>) => {
			onPointerDown?.(event);
			if (event.defaultPrevented || disabled || loading) {
				return;
			}
			if (event.button !== 0) {
				return;
			}
			rippleFromPointerRef.current = true;
			createRipple(event);
		},
		[createRipple, disabled, loading, onPointerDown],
	);

	const handleClick = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			if (disabled || loading) {
				event.preventDefault();
				return;
			}
			if (!rippleFromPointerRef.current) {
				createRipple(event);
			}
			rippleFromPointerRef.current = false;
			onClick?.(event);
		},
		[createRipple, disabled, loading, onClick],
	);

	const buttonTransition = {
		duration: 0.1,
		ease: [0.4, 0, 0.2, 1],
	} satisfies Transition;

	const resolvedWhileHover =
		disabled || loading || prefersReducedMotion
			? undefined
			: whileHoverProp === false
				? undefined
				: whileHoverProp !== undefined
					? whileHoverProp
					: appearance === "default"
						? { scale: 1.03, transition: buttonTransition }
						: undefined;

	const resolvedWhileTap =
		disabled || loading || prefersReducedMotion
			? undefined
			: whileTapProp === false
				? undefined
				: whileTapProp !== undefined
					? whileTapProp
					: appearance === "default"
						? { scale: 0.97, transition: buttonTransition }
						: undefined;

	const defaultChromeClass =
		appearance === "default"
			? cn(
					"relative overflow-hidden",
					"cursor-pointer disabled:!pointer-events-auto disabled:cursor-not-allowed",
					SQ_PRIMARY_CTA_GRADIENT_CLASS,
					!(disabled || loading) &&
						SQ_PRIMARY_CTA_GRADIENT_HOVER_CLASS,
					SQ_PRIMARY_CTA_GRADIENT_ACTIVE_CLASS,
					buttonVariants({ variant, size }),
					"text-amber-950 hover:text-amber-750 active:text-amber-950",
				)
			: "relative cursor-pointer disabled:!pointer-events-auto disabled:cursor-not-allowed";

	const rippleSpans = ripples.map((r) => (
		<motion.span
			key={r.id}
			aria-hidden
			className={cn(
				"pointer-events-none absolute rounded-full",
				"bg-white/45 dark:bg-white/35",
				"size-5",
				rippleClassName,
			)}
			initial={{ scale: 0, opacity: 0.5 }}
			animate={{ scale, opacity: 0 }}
			transition={transition}
			style={{
				top: r.y - RIPPLE_DIAMETER_PX / 2,
				left: r.x - RIPPLE_DIAMETER_PX / 2,
			}}
		/>
	));

	// `asChild` is supported by `ButtonProps` (and used by consumers like `PrimaryCta`).
	// `motion.button` cannot render via Radix Slot, so we fall back to `Button` when slotted.
	// This avoids leaking `asChild` as a DOM attribute and preserves navigation semantics.
	if (asChild) {
		return (
			<Button
				{...(props as Omit<ButtonProps, "asChild">)}
				asChild
				variant={variant}
				size={size}
				loading={loading}
				disabled={disabled}
				className={cn(buttonVariants({ variant, size }), className)}
				ref={ref}
			>
				{children}
			</Button>
		);
	}

	return (
		<motion.button
			{...props}
			ref={setButtonRef}
			data-slot="ripple-button"
			data-appearance={appearance}
			onPointerDown={handlePointerDown}
			onClick={handleClick}
			disabled={disabled || loading}
			aria-busy={loading ? true : undefined}
			initial={initial}
			animate={animate}
			/** Hover/tap scale sets its own `transition` on the gesture; a root default can bleed into layout/CSS with `h-auto` + gradient. */
			transition={undefined}
			whileHover={resolvedWhileHover}
			whileTap={resolvedWhileTap}
			className={cn(
				defaultChromeClass,
				className,
				// Loading: one line for spinner + label. Avoid `w-max` so host `w-full` (e.g. waitlist submit) is not overridden.
				loading &&
					"inline-flex min-w-0 max-w-full flex-nowrap items-center justify-center gap-1.5 whitespace-nowrap",
				// Keep pointer events so disabled buttons still show not-allowed cursor.
				// `!pointer-events-none` made Stage CTAs look clickable while clicks fell through (silent no-ops).
				loading && "!pointer-events-none",
			)}
		>
			{loading ? (
				<Spinner className="size-4 shrink-0 text-inherit" />
			) : null}
			{children}
			{appearance === "plain" ? (
				<span
					aria-hidden
					className="pointer-events-none absolute inset-0 z-[30] overflow-hidden rounded-[inherit]"
				>
					{rippleSpans}
				</span>
			) : (
				rippleSpans
			)}
		</motion.button>
	);
});

RippleButton.displayName = "RippleButton";
